import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, ClipboardList, Fuel, Wrench, CalendarCheck, Car, Users, BarChart3, Settings as SettingsIcon, MapPin, Map } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { filterOperations } from '@/utils/operationAccess';
import { transportApi, isManager, isDriver } from '@/services/transportApi';

const TransportDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const manager = isManager();
  const driver = isDriver();

  useEffect(() => { transportApi.dashboard().then(setStats).catch(() => {}); }, []);

  const allTiles = [
    { id: 'transport-my-tasks', title: 'My Tasks', description: 'Your assigned trips, Start/End & attendance', icon: ClipboardList, iconBg: 'bg-amber-500', route: '/sections/transport/my-tasks', testId: 'transport-my-tasks-tile' },
    { id: 'transport-requests', title: 'Request Board', description: 'Assign, override & track vehicle requests', icon: MapPin, iconBg: 'bg-indigo-500', route: '/sections/transport/requests', testId: 'transport-requests-tile', managerOnly: true },
    { id: 'transport-dispatch', title: 'Dispatch Map', description: 'Live vehicle pins + smart suggestions', icon: Map, iconBg: 'bg-sky-600', route: '/sections/transport/dispatch', testId: 'transport-dispatch-tile', managerOnly: true },
    { id: 'transport-locations', title: 'Saved Locations', description: 'Company sites & stops pinned on a map', icon: MapPin, iconBg: 'bg-lime-600', route: '/sections/transport/locations', testId: 'transport-locations-tile', managerOnly: true },
    { id: 'transport-trips', title: 'Trip Log', description: 'All trips with distance & duration', icon: Truck, iconBg: 'bg-teal-600', route: '/sections/transport/trips', testId: 'transport-trips-tile' },
    { id: 'transport-fuel', title: 'Fuel Log', description: 'Fuel fills with bill photos', icon: Fuel, iconBg: 'bg-orange-500', route: '/sections/transport/fuel', testId: 'transport-fuel-tile' },
    { id: 'transport-service', title: 'Service Log', description: 'Service / repair / tyre entries', icon: Wrench, iconBg: 'bg-rose-500', route: '/sections/transport/service', testId: 'transport-service-tile' },
    { id: 'transport-attendance', title: 'Attendance', description: 'Driver GPS punch & trip tags', icon: CalendarCheck, iconBg: 'bg-emerald-600', route: '/sections/transport/attendance', testId: 'transport-attendance-tile', managerOnly: true },
    { id: 'transport-vehicles', title: 'Vehicles', description: 'Vehicle master & eligible drivers', icon: Car, iconBg: 'bg-slate-600', route: '/sections/transport/vehicles', testId: 'transport-vehicles-tile', managerOnly: true },
    { id: 'transport-drivers', title: 'Drivers', description: 'Driver master & licences', icon: Users, iconBg: 'bg-purple-600', route: '/sections/transport/drivers', testId: 'transport-drivers-tile', managerOnly: true },
    { id: 'transport-reports', title: 'Reports & KPIs', description: 'Day / vehicle / driver / route · loading & unloading · Km/L', icon: BarChart3, iconBg: 'bg-cyan-700', route: '/sections/transport/reports', testId: 'transport-reports-tile', managerOnly: true },
    { id: 'transport-settings', title: 'Settings', description: 'Hired portfolio, deviation threshold, types', icon: SettingsIcon, iconBg: 'bg-gray-700', route: '/sections/transport/settings', testId: 'transport-settings-tile', managerOnly: true },
  ];
  let tiles = filterOperations('transport', allTiles);
  if (!manager) tiles = tiles.filter(t => !t.managerOnly);
  // drivers get My Tasks first
  if (driver) tiles = [...tiles.filter(t => t.id === 'transport-my-tasks'), ...tiles.filter(t => t.id !== 'transport-my-tasks')];

  const Stat = ({ label, value, testId }) => (
    <div className="bg-white/10 rounded-lg px-4 py-2 text-center" data-testid={testId}>
      <div className="text-2xl font-black">{value ?? '—'}</div>
      <div className="text-[11px] uppercase tracking-wide text-[#E8DCC8]">{label}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-dashboard-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="text-xl font-bold font-display">Internal Transport</h1>
              <p className="text-sm text-[#E8DCC8]">Fleet operations — trips, fuel, service, attendance & requests</p>
            </div>
          </div>
          {manager && (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              <Stat label="Active Vehicles" value={stats?.vehicles_active} testId="stat-vehicles" />
              <Stat label="Active Drivers" value={stats?.drivers_active} testId="stat-drivers" />
              <Stat label="Pending Reqs" value={stats?.pending_requests} testId="stat-pending" />
              <Stat label="On Trip" value={stats?.trips_in_transit} testId="stat-ontrip" />
              <Stat label="Present Today" value={stats?.drivers_present_today} testId="stat-present" />
            </div>
          )}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <Card key={tile.id} className="bg-white hover:shadow-lg transition-all duration-200 cursor-pointer border-0 shadow-md"
                onClick={() => navigate(tile.route)} data-testid={tile.testId}>
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className={`w-16 h-16 ${tile.iconBg} rounded-2xl flex items-center justify-center mb-4 shadow-md`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm leading-tight mb-2">{tile.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{tile.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default TransportDashboard;
